import { getCarApiPayment } from './carApi.js';
import { getOpenWalletLink, getCreditCTALink } from '../links.js';
import { getLinkMarkup, getPaymentMarkup } from '../markup.js';
import { USER_SHOW_PAYMENTS_KEY } from '../../global-constants.js';
import { isWIAPILoaded } from '../../utils/wait-for.js';
import { getDealerDRLeaseEnabled } from '../../helpers/index.js';

const getInterestRate = (rates, inventoryType) => {
	const defaultRate = '4.9';

	if (!rates) {
		return defaultRate;
	}

	let interestRate = rates?.custom > -1 || typeof rates?.custom === 'string'
		? rates.custom
		: rates?.[inventoryType];

	if (!interestRate && interestRate !== 0) {
		interestRate = rates?.new ? rates.new : defaultRate;
	}

	return interestRate;
}

export const getPaymentData = async (API, paymentDataSource, vehicle, config, dealerData, walletData, ePriceData, isNewCarBoostPayment) => {

	const noPayment = {
		financePayment: {},
		leasePayment: {},
	};
	await isWIAPILoaded();
	const userShowPayments = JSON.parse(localStorage.getItem(USER_SHOW_PAYMENTS_KEY));
	const shouldShowPayments = userShowPayments?.show;
	if (!shouldShowPayments && !isNewCarBoostPayment) {
		return noPayment;
	}

	const interestRate = getInterestRate(walletData.interestRate, vehicle?.inventoryType);
	const downPaymentPercent = typeof walletData.downPaymentPercent === 'number' ? walletData.downPaymentPercent : 0.2;
	const calculatedDownPayment = vehicle?.finalPrice ? Math.round(vehicle.finalPrice * downPaymentPercent) : 0;
	const downPayment = typeof walletData.downPayment === 'number' ? walletData.downPayment : calculatedDownPayment;

	const downPaymentData = {
		downPayment,
		downPaymentPercent,
		interestRate
	}

	if (walletData.loanTerm) {
		const {
			authToken,
			environment,
			alternatePaymentEndpoint
		} = config;

		try {
			return getCarApiPayment(vehicle, walletData, downPaymentData, dealerData, authToken, environment, alternatePaymentEndpoint, ePriceData, config);
		} catch (err) {
			API.log('Unable to get payment for vehicle:', err, vehicle.vin);
			return {
				noResult: true
			};
		}
	}

	return noPayment;
}

export const getPaymentContent = (
	config, 
	paymentData, 
	vehicle, 
	disclaimer, 
	isGridLayout, 
	isSRP, 
	walletData, 
	isMobile, 
	isNewCarBoostPayment, 
	isNewComparePage
) => {
	const { detailsLabel, showEstimatedPaymentLabel, applyNow, showApplyNow, refreshDsAfterSdpClose, newCarBoostPaymentLabel } = config;
	const {
		financePayment,
		leasePayment
	} = paymentData;
	const { paymentOption } = walletData;
	const isPaymentOptionsEnabled = getDealerDRLeaseEnabled();

	let selectedPanel;
	let vehicleData;
	let walletLinkLabel;

	let payment;
	const paymentFrequency = 'mo';

	// display selected payment option either finance or lease, with no fallback
	if (paymentOption === 'lease') {
		payment = leasePayment?.payment;
		walletLinkLabel = 'Lease Details';
	} else {
		payment = financePayment?.payment;
		walletLinkLabel = 'Details';
	}

	// Include the 'Show Estimated Payment' or 'Details' link.
	if (payment) {
		selectedPanel = 'paymentsPanel';
		vehicleData = vehicle;
		walletLinkLabel = isPaymentOptionsEnabled ? walletLinkLabel : detailsLabel;
	} else {
		walletLinkLabel = showEstimatedPaymentLabel;
	}

	let linkClass = '';
	if (isNewCarBoostPayment) {
		linkClass = 'text-muted text-decoration-none text-decoration-underline font-weight-normal ddc-font-size-xsmall';
	}

	const openWalletLink = getOpenWalletLink(
		walletLinkLabel, 
		linkClass, 
		!!payment, 
		vehicleData, 
		paymentData, 
		selectedPanel, 
		isNewComparePage
	);

	const openCreditAppLink = showApplyNow && !isSRP
		? getCreditCTALink(applyNow, 'd-block', refreshDsAfterSdpClose)
		: null;

	if (payment || isNewCarBoostPayment) {
		// display payments link on SRP
		// display payments link and credit app link on VDP
		return getPaymentMarkup(
			isGridLayout, 
			payment, 
			paymentFrequency, 
			disclaimer, 
			openWalletLink, 
			openCreditAppLink, 
			isMobile, 
			isNewCarBoostPayment, 
			newCarBoostPaymentLabel, 
			isSRP, 
			isNewComparePage
		);
	}

	// when payment is not available (finance or lease)
	// or the user has not interacted with wallet
	// display default link and credit app link
	return getLinkMarkup(openWalletLink, openCreditAppLink, isNewComparePage);
}
